// The vault signer: durable leaf allocation under the section 10 rules and the
// R11 order (reserve -> sign and persist -> simulate -> broadcast), the R5
// successor check, the section 9 rotation policy and the recovery-only mode of
// a restored device. The caller simulates and broadcasts; the signer never
// frees a leaf because a simulation failed, never signs a second digest on a
// leaf that has a record, and retires a tree once one of its leaves burned (R13).
// Every ordinary authorization, sync and resend carries its own current-slot
// observation rather than relying on one cached at open or at an earlier call;
// an observation may only move the signer's notion of the current slot
// forward, never backward, and the marker-expiry decision is made against it,
// atomically with the reservation (R25).

import { type EarlierEvidence, type NotAppliedReason, appliedBy } from './applied.js';
import { InThreadBackend, type SigningBackend } from './backend.js';
import type { Master } from './derive.js';
import { type AccountMetaSpec, type ExecHeaderSpec, type InnerIx, closeDigest, closeInstructionData, executeAccounts, executeDigest, executeInstructionData, fromHex, headerBytes, hex, parseHeader } from './digest.js';
import { type LeafRecord, type LeafStatus, LedgerConflict, type LedgerStore, type Purpose, type RecordStep, type ReserveOutcome, type StoredCloseOp, type StoredOp, TERMINAL, type TransitionResult, type TreeOrigin, UNRESOLVED, markerKey, recordKey } from './ledger.js';
import { RECOVERY_LEAF, RECOVERY_VALID_UNTIL, TAG_CLOSED, TAG_VAULT, type VaultSnapshot, equalBytes, leafUsed, recoveryHeader, snapshot, usedCount } from './recovery.js';

/** Used leaves at which the next authorization must carry NEXT_KEY (section 9). */
export const ROTATE_AT = 200;
/** Used leaves at which the app should pre-compute tree t + 1 in the background. */
export const PRECOMPUTE_AT = 150;
/** Slots a tree marker stays fresh for from the slot it was written or last renewed, about one hour (R20). Past this, a device that has not synced must rotate through recovery first. */
export const MARKER_LIFETIME_SLOTS = 9_000n;
/** Trees below the current one that reconciliation reads (docs/17 section 3): T - 8 to T - 1, the window the app's history shows. */
export const EARLIER_TREES = 8;

export type SignerErrorCode = 'KeyMismatch' | 'RecoveryRequired' | 'Unresolved' | 'LeafConflict' | 'BadSuccessor' | 'NoLeaf' | 'NoRecord' | 'BadStatus' | 'NotBounded' | 'Expired' | 'Closed' | 'StaleSlot';

export class SignerError extends Error {
  constructor(readonly code: SignerErrorCode, message: string) {
    super(message);
    this.name = 'SignerError';
  }
}

export type Mode = 'normal' | 'recovery-only';

export interface AuthorizeInput {
  header: ExecHeaderSpec;
  aux: AccountMetaSpec[];
  inners: InnerIx[];
  purpose: Purpose;
  /**
   * The caller's current chain-slot observation for this authorization,
   * taken from the same RPC response that supplied the transaction's
   * blockhash (`getLatestBlockhash` returns `context.slot`). It must not be
   * older than the last slot this signer observed, at open or at any earlier
   * call; the signer decides marker expiry against it, atomically with the
   * reservation (R25).
   */
  currentSlot: bigint;
}

export interface Authorization {
  leaf: number;
  digest: Uint8Array;
  signature: Uint8Array;
  header: ExecHeaderSpec;
  /** The Execute instruction; pass both to the transaction builder. */
  instructionData: Uint8Array;
  accounts: AccountMetaSpec[];
  record: LeafRecord;
}

export interface CloseAuthorizeInput {
  destination: Uint8Array;
  validUntilSlot: bigint;
  /** This call's own chain-slot observation; see `AuthorizeInput.currentSlot` (R25). */
  currentSlot: bigint;
}

/** A Close carries no header and no successor: it is EXACT, never BOUNDED, and never rotates (a Close needs no `next_key`). */
export interface CloseAuthorization {
  leaf: number;
  digest: Uint8Array;
  signature: Uint8Array;
  instructionData: Uint8Array;
  accounts: AccountMetaSpec[];
}

export interface OpenInput {
  store: LedgerStore;
  /** The master (k, c), used to build an in-thread backend when `backend` is absent. Node tools and tests. */
  master?: Master;
  /**
   * Where trees are derived and digests signed (backend.ts). The app passes
   * a `WorkerBackend` so no secret is ever held on the main thread; with
   * neither this nor `master`, `open` refuses.
   */
  backend?: SigningBackend;
  programId: Uint8Array;
  vaultAddress: Uint8Array;
  /** The vault account's owner and data as read from the chain. */
  owner: Uint8Array;
  accountData: Uint8Array;
  /**
   * The chain's current slot at open: the signer's first slot observation,
   * used for the marker-expiry check (R20) and to seed `reserveOrdinary`'s
   * eligibility check. Every later `authorize`, `sync` or `resend` call
   * carries its own fresh observation (R25); none of them may be older than
   * this one.
   */
  currentSlot: bigint;
  /** CSPRNG for leaf choice; defaults to WebCrypto. */
  rng?: (n: number) => Uint8Array;
  now?: () => number;
}

/** What `resolveEarlier` judges (docs/17 sections 3 and 4). */
export interface ResolveEarlierInput {
  /**
   * The evidence for each `sent` candidate, by record key: what the session's
   * `EarlierReads` returned for the pair (record key, `txSignature`) the
   * candidate held when it was listed. A record whose stored `txSignature`
   * is not the evidence's is not judged in this pass.
   */
  evidence: ReadonlyMap<string, EarlierEvidence>;
  /** The vault account's data the caller has just used (`open` or `sync`), so the proof and the marker checks judge one snapshot. */
  accountData: Uint8Array;
  /** The slot observation that came with `accountData`; adopted in the queued job before any write, and refused if older than the signer's last (R25). */
  currentSlot: bigint;
}

/**
 * Why `resolveEarlier` left a record as it was. The `appliedBy` reasons
 * (rows 7-11), and: `contradiction`, the tree's proof contradicts the chain
 * (two records proven, or T = t + 1 with a key other than `nextKey`; row 5);
 * `vault-mismatch` and `tree-mismatch`, the snapshot is another vault's or
 * at another tree than the signer's (nothing at all is written);
 * `not-sent`, a `reserved` or `signed` record with no proven sibling, never
 * read (row 12); `no-tx-signature`, a `sent` record without one (row 11);
 * `no-evidence`, none was passed for it; `stale-evidence`, the record's
 * stored `txSignature` is not the one the evidence was read for (a later
 * retry, read at the next pass); `resolved-elsewhere`, another writer
 * resolved the row before this step's transition (docs/17 L8).
 */
export type EarlierUnchangedReason = NotAppliedReason | 'contradiction' | 'vault-mismatch' | 'tree-mismatch' | 'not-sent' | 'no-tx-signature' | 'no-evidence' | 'stale-evidence' | 'resolved-elsewhere';

/**
 * One candidate's outcome: `landed` (rows 2-4), `landed-marker` (row 1, the
 * successor marker written from the landing slot), `not-applied` (row 6,
 * burned), or `unchanged` with its reason. For tests and logs.
 */
export type EarlierOutcome = { key: string; treeIndex: number; leaf: number } & ({ outcome: 'landed' | 'landed-marker' | 'not-applied' } | { outcome: 'unchanged'; reason: EarlierUnchangedReason });

type Judged = { r: LeafRecord; proven: true; landingSlot: bigint } | { r: LeafRecord; proven: false; reason: EarlierUnchangedReason };

/**
 * Uniformly random leaf among those unused on chain, absent from the ledger
 * and below 255 (section 10). Deterministic policies collide with certainty
 * across two live devices; random choice over ~200 candidates collides with
 * probability about 1/200 per concurrent operation.
 */
export function pickLeaf(usedLeaves: Uint8Array, taken: ReadonlySet<number>, rng: (n: number) => Uint8Array): number | undefined {
  const candidates: number[] = [];
  for (let l = 0; l < RECOVERY_LEAF; l++) if (!leafUsed(usedLeaves, l) && !taken.has(l)) candidates.push(l);
  if (candidates.length === 0) return undefined;
  const limit = Math.floor(0x1_0000_0000 / candidates.length) * candidates.length;
  for (;;) {
    const r = new DataView(rng(4).buffer).getUint32(0);
    if (r < limit) return candidates[r % candidates.length];
  }
}

function webRng(n: number): Uint8Array {
  const out = new Uint8Array(n);
  globalThis.crypto.getRandomValues(out);
  return out;
}

function encodeOp(header: ExecHeaderSpec, aux: AccountMetaSpec[], inners: InnerIx[]): StoredOp {
  return {
    header: hex(headerBytes(header)),
    aux: aux.map((a) => ({ pubkey: hex(a.pubkey), isSigner: a.isSigner, isWritable: a.isWritable })),
    inners: inners.map((ix) => ({ program: hex(ix.program), metas: ix.metas.map((m) => ({ pubkey: hex(m.pubkey), isWritable: m.isWritable })), data: hex(ix.data) })),
  };
}

/** Structural discriminant: only `StoredCloseOp` lacks a `header` (kept minimal so the Execute encoding stays byte-compatible with existing stored records). */
function isCloseOp(op: StoredOp): op is StoredCloseOp {
  return !('header' in op);
}

function decodeOp(r: LeafRecord): { header: ExecHeaderSpec; aux: AccountMetaSpec[]; inners: InnerIx[] } {
  const op = r.op;
  if (!op || isCloseOp(op)) throw new SignerError('NoRecord', `record ${r.key} carries no Execute operation`);
  return {
    header: parseHeader(fromHex(op.header)).header,
    aux: op.aux.map((a) => ({ pubkey: fromHex(a.pubkey), isSigner: a.isSigner, isWritable: a.isWritable })),
    inners: op.inners.map((ix) => ({ program: fromHex(ix.program), metas: ix.metas.map((m) => ({ pubkey: fromHex(m.pubkey), isWritable: m.isWritable })), data: fromHex(ix.data) })),
  };
}

function encodeCloseOp(destination: Uint8Array, validUntilSlot: bigint): StoredOp {
  return { destination: hex(destination), validUntilSlot: validUntilSlot.toString() };
}

function decodeCloseOp(r: LeafRecord): { destination: Uint8Array; validUntilSlot: bigint } {
  const op = r.op;
  if (!op || !isCloseOp(op)) throw new SignerError('NoRecord', `record ${r.key} carries no Close operation`);
  return { destination: fromHex(op.destination), validUntilSlot: BigInt(op.validUntilSlot) };
}

/** The start of the note on a record the reconciliation step burns as not applied (row 6, docs/17 2.5); the app's History keys on it. */
export const NOT_APPLIED_NOTE_PREFIX = 'not applied';
/** The note `resolveEarlier` writes on a sibling of tree `tree` when the record on `leaf` is proven to have moved the tree on (row 6). */
export function notAppliedNote(tree: number, leaf: number): string {
  return `${NOT_APPLIED_NOTE_PREFIX}: tree ${tree} moved on through leaf ${leaf}`;
}

/**
 * The resolver's record transition (docs/17 L8; rows 1-4 and 6 of the state
 * table), keyed by record key rather than by the signer's current tree, for
 * a record on a tree the chain has left. `landed` moves only a `sent` row;
 * `burned` moves any unresolved row and sets `note`. It sets nothing else:
 * `updatedAt`, `txSignature`, `signature` and every other field are taken
 * from the row as stored at the commit, so a newer send another tab saved in
 * between survives (L2). On any other stored status it writes nothing, and
 * the store's `written: false` tells the caller another writer resolved the
 * row first. Pure and synchronous, as `transitionRecord` requires.
 */
export function resolverStep(to: 'landed' | 'burned', note?: string): RecordStep {
  const from: readonly LeafStatus[] = to === 'landed' ? ['sent'] : UNRESOLVED;
  return (stored) => {
    if (!stored || !from.includes(stored.status)) return undefined;
    const next: LeafRecord = { ...stored, status: to };
    if (note !== undefined) next.note = note;
    return next;
  };
}

/**
 * Where a device may sign ordinary operations: it holds a marker for the
 * tree that has not expired (`validUntilSlot >= currentSlot`, R20), and no
 * leaf of the tree was burned. A burn means a signature was exposed without
 * landing; the tree is retired and only its recovery message may be signed
 * until the rotation lands (R13). A restored backup or another device that
 * lacks the burned record then finds the chain a tree ahead and is forced
 * into recovery too, once its own marker also lapses if the exposing device
 * never lands the rotation. What no local rule can close is the window
 * between the exposure and the marker's expiry, or the rotation landing,
 * whichever comes first (docs/01 sections 10 and 14).
 */
async function modeFor(store: LedgerStore, vaultIdHex: string, treeIndex: number, currentSlot: bigint): Promise<Mode> {
  const marker = await store.getMarker(vaultIdHex, treeIndex);
  if (!marker || BigInt(marker.validUntilSlot) < currentSlot) return 'recovery-only';
  const records = await store.listRecords(vaultIdHex, treeIndex);
  return records.some((r) => r.status === 'burned') ? 'recovery-only' : 'normal';
}

export class VaultSigner {
  readonly programId: Uint8Array;
  readonly vaultAddress: Uint8Array;
  readonly vaultId: Uint8Array;
  readonly vaultIdHex: string;
  private readonly store: LedgerStore;
  private readonly backend: SigningBackend;
  private readonly owner: Uint8Array;
  private readonly rng: (n: number) => Uint8Array;
  private readonly now: () => number;
  private snap: VaultSnapshot;
  private verifyingKeyBytes: Uint8Array;
  private successorCache?: Uint8Array;
  private modeValue: Mode;
  private currentSlotValue: bigint;
  private closed = false;
  private closing = false;
  private queue: Promise<unknown> = Promise.resolve();

  private constructor(i: OpenInput, backend: SigningBackend, snap: VaultSnapshot, verifyingKey: Uint8Array, mode: Mode) {
    this.programId = i.programId;
    this.vaultAddress = i.vaultAddress;
    this.vaultId = snap.vaultId;
    this.vaultIdHex = hex(snap.vaultId);
    this.store = i.store;
    this.backend = backend;
    this.owner = i.owner;
    this.rng = i.rng ?? webRng;
    this.now = i.now ?? Date.now;
    this.snap = snap;
    this.verifyingKeyBytes = verifyingKey;
    this.modeValue = mode;
    this.currentSlotValue = i.currentSlot;
  }

  /**
   * Open the signer on a vault as it stands on chain. The derived key of the
   * chain's tree must equal the stored key (R5). Without a marker, with an
   * expired one (R20), or with a burned leaf on the tree, only `recover()`
   * may sign.
   */
  static async open(i: OpenInput): Promise<VaultSigner> {
    const snap = snapshot(i.programId, i.owner, i.accountData);
    if (!i.backend && !i.master) throw new SignerError('KeyMismatch', 'open needs a master or a signing backend');
    const backend = i.backend ?? new InThreadBackend(i.master!, i.programId);
    const verifyingKey = await backend.deriveTree(snap.treeIndex);
    if (!equalBytes(verifyingKey, snap.verifyingKey)) {
      await backend.wipeTree(snap.treeIndex);
      throw new SignerError('KeyMismatch', `the chain's key is not derive(${snap.treeIndex}) of this mnemonic`);
    }
    return new VaultSigner(i, backend, snap, verifyingKey, await modeFor(i.store, hex(snap.vaultId), snap.treeIndex, i.currentSlot));
  }

  /**
   * Record that this device saw tree `t` fresh. For t = 0 that is only after
   * this device's own Initialize is finalized and successful and a finalized
   * read shows the vault untouched on tree 0 (docs/18-one-fee-reserve.md 5.1 D,
   * R79): never before or during the send, which needs no marker, since the
   * fee reserve's key signs Initialize. Rotations and recoveries this
   * signer lands write their own markers in `sync`, and `resolveEarlier`
   * writes one for a rotation the ledger's own transaction is proven to have
   * applied, from that transaction's landing slot S rather than a later
   * observation (docs/17 section 4, M1-M6); never write one by hand for any
   * other tree. Valid until `currentSlot + MARKER_LIFETIME_SLOTS` (R20);
   * `sync` renews it the same way while the tree stays normal.
   */
  static async markTreeFresh(store: LedgerStore, vaultId: Uint8Array, treeIndex: number, origin: TreeOrigin, currentSlot: bigint, now: () => number = Date.now): Promise<void> {
    const id = hex(vaultId);
    await store.putMarker({ key: markerKey(id, treeIndex), vaultId: id, treeIndex, origin, validUntilSlot: (currentSlot + MARKER_LIFETIME_SLOTS).toString(), createdAt: now() });
  }

  get mode(): Mode {
    return this.modeValue;
  }
  get treeIndex(): number {
    return this.snap.treeIndex;
  }
  get chain(): VaultSnapshot {
    return this.snap;
  }
  get usedCount(): number {
    return usedCount(this.snap.usedLeaves);
  }
  get verifyingKey(): Uint8Array {
    return this.verifyingKeyBytes;
  }
  /** True once the wipe has run; the terminal state (R21). */
  get isClosed(): boolean {
    return this.closed;
  }
  /** True from the moment `close()` is called; every other call rejects from this instant, even one already queued behind in-flight work (R21). */
  get isClosing(): boolean {
    return this.closing;
  }
  /**
   * `derive(t + 1)`: the only key a rotation from this tree may install (R5).
   * Derived by the backend, which then holds tree t + 1 ready for the
   * rotation it will be needed for; `sync` forgets the old tree once the
   * chain has moved on.
   */
  async successor(): Promise<Uint8Array> {
    this.successorCache ??= await this.backend.deriveTree(this.snap.treeIndex + 1);
    return this.successorCache;
  }

  /** Every state transition runs here, one at a time: an awaited storage operation never resumes against a different key or tree (R14). */
  private run<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.queue.then(fn, fn);
    this.queue = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }
  /**
   * Checked both synchronously at the top of every public method (so a call
   * made after `close()` was invoked rejects immediately, without waiting
   * for the queue to reach it) and again at the start of each queued job
   * (so a call merely queued, not yet started, before `close()` was invoked
   * is rejected too, rather than run against a wiped seed) (R21).
   */
  private live(): void {
    if (this.closing || this.closed) throw new SignerError('Closed', 'the signer is closed; open a new one');
  }
  /**
   * Adopt `slot` as the signer's current slot, or refuse it (R25). A slot
   * observation can never move the decision backward: it must be at least
   * the last slot this signer observed, at open or at any earlier call, or
   * this throws `StaleSlot` naming both slots. Called at the start of the
   * queued job for `authorize`, `sync` and `resend`, so the comparison uses
   * the state at execution time, not at issue time.
   */
  private observeSlot(slot: bigint): bigint {
    if (slot < this.currentSlotValue) throw new SignerError('StaleSlot', `observed slot ${slot} is older than the last slot this signer observed, ${this.currentSlotValue}`);
    this.currentSlotValue = slot;
    return this.currentSlotValue;
  }
  /** Unresolved records of the current tree; nothing new is signed while any exist. */
  async pending(): Promise<LeafRecord[]> {
    this.live();
    return (await this.store.listRecords(this.vaultIdHex, this.snap.treeIndex)).filter((r) => UNRESOLVED.includes(r.status));
  }
  private async taken(): Promise<Set<number>> {
    return new Set((await this.store.listRecords(this.vaultIdHex, this.snap.treeIndex)).map((r) => r.leaf));
  }
  private async record(leaf: number): Promise<LeafRecord> {
    const r = await this.store.getRecord(recordKey(this.vaultIdHex, this.snap.treeIndex, leaf));
    if (!r) throw new SignerError('NoRecord', `no record for leaf ${leaf} of tree ${this.snap.treeIndex}`);
    return r;
  }
  private freeLeaves(): number {
    let n = 0;
    for (let l = 0; l < RECOVERY_LEAF; l++) if (!leafUsed(this.snap.usedLeaves, l)) n++;
    return n;
  }
  /**
   * Whether the next `authorize` will install `derive(t + 1)` on its own
   * (the rotation policy in `authorizeInner`: `ROTATE_AT` used leaves, or
   * one free leaf left). A caller that sizes or displays a transaction
   * before signing asks this first and passes `successor()` as the header's
   * `nextKey`, so what it sizes, shows and signs is one and the same header
   * (R43).
   */
  get rotationDue(): boolean {
    return this.usedCount >= ROTATE_AT || this.freeLeaves() <= 1;
  }

  /**
   * Reserve a leaf, sign, persist, return (R11). The caller simulates the
   * instruction and broadcasts only on success; a failure is reported with
   * `markBurned` (EXACT) or retried with `resend` (BOUNDED). At `ROTATE_AT`
   * used leaves, or with one free leaf left, the header carries `derive(t + 1)`;
   * a caller-supplied `nextKey` must equal it (R5). `input.currentSlot` is
   * this call's own chain-slot observation, adopted at the start of the
   * queued job and used, atomically with the reservation, for the marker's
   * expiry check (R25); a slot older than the last one this signer observed
   * rejects with `StaleSlot` before anything is reserved.
   */
  async authorize(input: AuthorizeInput): Promise<Authorization> {
    this.live();
    return this.run(() => this.authorizeInner(input));
  }

  private async authorizeInner(input: AuthorizeInput): Promise<Authorization> {
    this.live();
    const slot = this.observeSlot(input.currentSlot);
    // A tombstone accepts exactly one operation on chain, a NEXT_KEY-only Reopen, and only `recover()` builds that message; every other authorization is refused here before it reserves anything.
    if (this.snap.tag === TAG_CLOSED) throw new SignerError('RecoveryRequired', 'the vault is closed on chain (tag = 2); run recover() to reopen it, then authorize again');
    // Fast local checks for a good error message; the authoritative decision is `reserveOrdinary` below (R22), which is checked atomically against the store's own contents, including another tab's writes this signer's cached `modeValue` cannot see.
    if (this.modeValue !== 'normal') throw new SignerError('RecoveryRequired', 'this tree is not signable on this device (no marker, an expired one, or one of its leaves was exposed without landing); run recover() and let the rotation land first');
    const pending = await this.pending();
    if (pending.length) throw new SignerError('Unresolved', `leaf ${pending[0]!.leaf} is ${pending[0]!.status}; resolve it before signing again`);
    const header: ExecHeaderSpec = { ...input.header };
    const successor = await this.successor();
    if (header.nextKey) {
      if (!equalBytes(header.nextKey, successor)) throw new SignerError('BadSuccessor', 'next_key is not derive(t + 1) of this mnemonic (R5)');
    } else if (this.usedCount >= ROTATE_AT || this.freeLeaves() <= 1) {
      header.nextKey = successor;
    }
    const taken = await this.taken();
    for (let attempt = 0; attempt < 4; attempt++) {
      const leaf = pickLeaf(this.snap.usedLeaves, taken, this.rng);
      if (leaf === undefined) throw new SignerError('NoLeaf', 'no free leaf below 255; the tree must rotate through recovery');
      const digest = executeDigest({ programId: this.programId, vaultId: this.vaultId, vaultAddress: this.vaultAddress, treeIndex: this.snap.treeIndex, leaf, header, aux: input.aux.map((a) => a.pubkey), inners: input.inners });
      const record = this.newRecord(leaf, digest, input.purpose, header.validUntilSlot, header.nextKey, encodeOp(header, input.aux, input.inners));
      // Atomic eligibility check + insert: another tab or signer on the same ledger cannot hold this leaf too, sign on a retired tree, or sign past its marker's expiry (R14, R22).
      const outcome: ReserveOutcome = await this.store.reserveOrdinary(record, slot);
      if (outcome.ok) return this.signRecord(record, header, input.aux, input.inners);
      if (outcome.reason === 'exists') {
        taken.add(leaf);
        continue;
      }
      if (outcome.reason === 'unresolved') throw new SignerError('Unresolved', 'another record on this tree is unresolved; resolve it before signing again');
      this.modeValue = 'recovery-only';
      throw new SignerError('RecoveryRequired', `this tree is not signable on this device (${outcome.reason}); run recover() and let the rotation land first`);
    }
    throw new SignerError('LeafConflict', 'another writer reserved every leaf this signer chose; resolve the shared ledger first');
  }

  /**
   * Reserve a leaf, sign, persist, return: the Close analogue of `authorize`
   * (R11). A Close is EXACT (never BOUNDED) and carries no header, so unlike
   * `authorize` it never installs `next_key` and never checks the rotation
   * policy (a Close needs no successor). The caller simulates and broadcasts
   * only on success; a failed simulation is reported with `markBurned`, the
   * same as any other EXACT authorization. `input.currentSlot` is this
   * call's own chain-slot observation (R25), checked atomically with the
   * reservation.
   */
  async authorizeClose(input: CloseAuthorizeInput): Promise<CloseAuthorization> {
    this.live();
    return this.run(() => this.authorizeCloseInner(input));
  }

  private async authorizeCloseInner(input: CloseAuthorizeInput): Promise<CloseAuthorization> {
    this.live();
    const slot = this.observeSlot(input.currentSlot);
    if (this.snap.tag === TAG_CLOSED) throw new SignerError('RecoveryRequired', 'the vault is already closed on chain; run recover() to reopen it, close() cannot run twice');
    if (this.modeValue !== 'normal') throw new SignerError('RecoveryRequired', 'this tree is not signable on this device (no marker, an expired one, or one of its leaves was exposed without landing); run recover() and let the rotation land first');
    const pending = await this.pending();
    if (pending.length) throw new SignerError('Unresolved', `leaf ${pending[0]!.leaf} is ${pending[0]!.status}; resolve it before signing again`);
    const taken = await this.taken();
    for (let attempt = 0; attempt < 4; attempt++) {
      const leaf = pickLeaf(this.snap.usedLeaves, taken, this.rng);
      if (leaf === undefined) throw new SignerError('NoLeaf', 'no free leaf below 255; the tree must rotate through recovery');
      const digest = closeDigest({ programId: this.programId, vaultId: this.vaultId, vaultAddress: this.vaultAddress, treeIndex: this.snap.treeIndex, leaf, validUntilSlot: input.validUntilSlot, destination: input.destination });
      const record = this.newRecord(leaf, digest, 'close', input.validUntilSlot, undefined, encodeCloseOp(input.destination, input.validUntilSlot));
      const outcome: ReserveOutcome = await this.store.reserveOrdinary(record, slot);
      if (outcome.ok) return this.signCloseRecord(record, input.destination, input.validUntilSlot);
      if (outcome.reason === 'exists') {
        taken.add(leaf);
        continue;
      }
      if (outcome.reason === 'unresolved') throw new SignerError('Unresolved', 'another record on this tree is unresolved; resolve it before signing again');
      this.modeValue = 'recovery-only';
      throw new SignerError('RecoveryRequired', `this tree is not signable on this device (${outcome.reason}); run recover() and let the rotation land first`);
    }
    throw new SignerError('LeafConflict', 'another writer reserved every leaf this signer chose; resolve the shared ledger first');
  }

  private newRecord(leaf: number, digest: Uint8Array, purpose: Purpose, validUntilSlot: bigint, nextKey: Uint8Array | undefined, op: StoredOp): LeafRecord {
    const t = this.now();
    const r: LeafRecord = { key: recordKey(this.vaultIdHex, this.snap.treeIndex, leaf), vaultId: this.vaultIdHex, treeIndex: this.snap.treeIndex, leaf, digest: hex(digest), purpose, status: 'reserved', validUntilSlot: validUntilSlot.toString(), op, createdAt: t, updatedAt: t };
    if (nextKey) r.nextKey = hex(nextKey);
    return r;
  }

  /**
   * Sign the digest a record reserved (identical bytes on every call) and
   * persist before returning. The stored reservation is read first for the
   * digest to sign; the commit is one `transitionRecord` (docs/17 L8) that
   * checks the row as stored at that moment, so a replaced or foreign record
   * is a conflict, never a second digest on the leaf, and a row another
   * writer resolved meanwhile is refused `BadStatus` with no authorization
   * returned.
   */
  private async signRecord(record: LeafRecord, header: ExecHeaderSpec, aux: AccountMetaSpec[], inners: InnerIx[]): Promise<Authorization> {
    const stored = await this.store.getRecord(record.key);
    if (!stored || stored.digest !== record.digest) throw new SignerError('LeafConflict', `the reservation of leaf ${record.leaf} was replaced or removed`);
    const digest = fromHex(stored.digest);
    const { signature: sig, saltTrial } = await this.backend.sign(this.snap.treeIndex, stored.leaf, digest);
    const committed = await this.commitSignature(stored, sig, saltTrial);
    return { leaf: stored.leaf, digest, signature: sig, header, instructionData: executeInstructionData(sig, header, inners), accounts: executeAccounts(this.vaultAddress, aux, inners), record: committed };
  }

  /** The Close analogue of `signRecord`: same persist-before-exposure discipline, `closeInstructionData` in place of `executeInstructionData`. */
  private async signCloseRecord(record: LeafRecord, destination: Uint8Array, validUntilSlot: bigint): Promise<CloseAuthorization> {
    const stored = await this.store.getRecord(record.key);
    if (!stored || stored.digest !== record.digest) throw new SignerError('LeafConflict', `the reservation of leaf ${record.leaf} was replaced or removed`);
    const digest = fromHex(stored.digest);
    const { signature: sig, saltTrial } = await this.backend.sign(this.snap.treeIndex, stored.leaf, digest);
    await this.commitSignature(stored, sig, saltTrial);
    const accounts: AccountMetaSpec[] = [
      { pubkey: this.vaultAddress, isSigner: false, isWritable: true },
      { pubkey: destination, isSigner: false, isWritable: true },
    ];
    return { leaf: stored.leaf, digest, signature: sig, instructionData: closeInstructionData(sig, validUntilSlot), accounts };
  }

  /**
   * The signing writer's transition (docs/17 L8), against the row as stored
   * at the commit: `reserved` becomes `signed` with `signature`, `saltTrial`
   * and `updatedAt`; a `signed` or `sent` row whose stored signature equals
   * the one just computed is left exactly as stored (another tab's
   * `markSent` keeps its `txSignature` and date); one with no stored
   * signature, which only an import or a hand-written row produces, gets
   * `signature` and `saltTrial` with its status and `updatedAt` kept. A
   * different stored signature is `LeafConflict`; a `landed` or `burned` row
   * is `BadStatus`, writing nothing. Returns the row as stored afterwards.
   */
  private async commitSignature(read: LeafRecord, sig: Uint8Array, saltTrial: number): Promise<LeafRecord> {
    const sigHex = hex(sig);
    const t = this.now();
    const { record } = await this.transition(read.key, (cur) => {
      if (!cur || cur.digest !== read.digest) throw new SignerError('LeafConflict', `the reservation of leaf ${read.leaf} was replaced or removed`);
      if (TERMINAL.includes(cur.status)) throw new SignerError('BadStatus', `leaf ${cur.leaf} of tree ${cur.treeIndex} is ${cur.status}; its signature is not returned`);
      if (cur.signature !== undefined && cur.signature !== sigHex) throw new SignerError('LeafConflict', `stored signature of leaf ${cur.leaf} differs from the deterministic one`);
      if (cur.status === 'reserved') return { ...cur, status: 'signed', signature: sigHex, saltTrial, updatedAt: Math.max(cur.updatedAt, t) };
      if (cur.signature === undefined) return { ...cur, signature: sigHex, saltTrial };
      return undefined;
    });
    return record!;
  }

  /** One record write through the store's primitive (docs/17 L8); the store's own refusals (`LedgerConflict`) reach the caller as `LeafConflict`, as `commit` did. */
  private async transition(key: string, step: RecordStep): Promise<TransitionResult> {
    try {
      return await this.store.transitionRecord(key, step);
    } catch (e) {
      if (e instanceof LedgerConflict) throw new SignerError('LeafConflict', e.message);
      throw e;
    }
  }

  /** Resume an unresolved record after a crash: a `reserved` one is signed now (same bytes as any earlier attempt), `signed`/`sent` return the stored signature. A `purpose: 'close'` record resumes through the Close path and returns a `CloseAuthorization`. */
  async resume(leaf: number): Promise<Authorization | CloseAuthorization> {
    this.live();
    return this.run(async () => {
      this.live();
      const r = await this.record(leaf);
      if (!UNRESOLVED.includes(r.status)) throw new SignerError('BadStatus', `leaf ${leaf} is ${r.status}`);
      if (r.purpose === 'close') {
        const op = decodeCloseOp(r);
        return this.signCloseRecord(r, op.destination, op.validUntilSlot);
      }
      const op = decodeOp(r);
      return this.signRecord(r, op.header, op.aux, op.inners);
    });
  }

  /**
   * BOUNDED retry: the identical signature and header with a fresh route.
   * Refused for EXACT records; past `valid_until_slot` the record is burned
   * instead. `currentSlot` is this call's own observation, adopted at the
   * start of the queued job (R25); it may not be older than the last slot
   * this signer observed.
   */
  async resend(leaf: number, inners: InnerIx[], currentSlot: bigint): Promise<Authorization> {
    this.live();
    return this.run(async () => {
      this.live();
      const slot = this.observeSlot(currentSlot);
      const r = await this.record(leaf);
      if (r.purpose !== 'swap-bounded') throw new SignerError('NotBounded', `leaf ${leaf} is an EXACT authorization; a failed EXACT attempt is burned, never re-sent`);
      if (r.status !== 'signed' && r.status !== 'sent') throw new SignerError('BadStatus', `leaf ${leaf} is ${r.status}`);
      if (BigInt(r.validUntilSlot) < slot) {
        await this.burn(leaf, 'expired before landing');
        throw new SignerError('Expired', `leaf ${leaf} expired at slot ${r.validUntilSlot}`);
      }
      const op = decodeOp(r);
      const sig = fromHex(r.signature!);
      return { leaf, digest: fromHex(r.digest), signature: sig, header: op.header, instructionData: executeInstructionData(sig, op.header, inners), accounts: executeAccounts(this.vaultAddress, op.aux, inners), record: r };
    });
  }

  /** The tree's recovery message (section 9, R8): leaf 255, NEXT_KEY = derive(t + 1), no expiry. Allowed in every mode, idempotent, the same bytes on every device. A recovery record already `landed` or `burned` is refused `BadStatus` at the commit, and no authorization is returned (docs/17 L8). */
  async recover(): Promise<Authorization> {
    this.live();
    return this.run(async () => {
      this.live();
      const header = recoveryHeader(await this.successor());
      const digest = executeDigest({ programId: this.programId, vaultId: this.vaultId, vaultAddress: this.vaultAddress, treeIndex: this.snap.treeIndex, leaf: RECOVERY_LEAF, header, aux: [], inners: [] });
      const key = recordKey(this.vaultIdHex, this.snap.treeIndex, RECOVERY_LEAF);
      let record = await this.store.getRecord(key);
      if (!record) {
        if (leafUsed(this.snap.usedLeaves, RECOVERY_LEAF)) throw new SignerError('LeafConflict', 'leaf 255 is already spent on chain; the tree must have rotated');
        const fresh = this.newRecord(RECOVERY_LEAF, digest, 'recovery', RECOVERY_VALID_UNTIL, header.nextKey, encodeOp(header, [], []));
        record = (await this.store.reserve(fresh)) ? fresh : await this.store.getRecord(key);
      }
      if (!record || record.digest !== hex(digest)) throw new SignerError('LeafConflict', 'leaf 255 carries a digest that is not the recovery message');
      return this.signRecord(record, header, [], []);
    });
  }

  /**
   * A status change on the current tree as one transition (docs/17 L8): the
   * status check runs against the row as stored at the commit, and only
   * `status`, `patch`'s fields and `updatedAt` (never earlier than stored)
   * are set; every other field is the stored row's.
   */
  private async setStatus(leaf: number, from: readonly LeafStatus[], to: LeafStatus, patch: { txSignature?: string; note?: string } = {}): Promise<LeafRecord> {
    const tree = this.snap.treeIndex;
    const t = this.now();
    const { record } = await this.transition(recordKey(this.vaultIdHex, tree, leaf), (cur) => {
      if (!cur) throw new SignerError('NoRecord', `no record for leaf ${leaf} of tree ${tree}`);
      if (!from.includes(cur.status)) throw new SignerError('BadStatus', `leaf ${leaf} is ${cur.status}, cannot become ${to}`);
      return { ...cur, ...patch, status: to, updatedAt: Math.max(cur.updatedAt, t) };
    });
    return record!;
  }
  /**
   * `sync`'s resolution of one record it listed as unresolved: the same
   * transition as `setStatus`, except that a row another writer resolved
   * meanwhile (or, impossibly, removed) is left as stored and the sync goes
   * on. `written` says whether this call's own result was stored.
   */
  private async syncResolve(r: LeafRecord, to: 'landed' | 'burned', note?: string): Promise<TransitionResult> {
    const t = this.now();
    return this.transition(r.key, (cur) => {
      if (!cur || !UNRESOLVED.includes(cur.status)) return undefined;
      const next: LeafRecord = { ...cur, status: to, updatedAt: Math.max(cur.updatedAt, t) };
      if (note !== undefined) next.note = note;
      return next;
    });
  }
  /** A burn retires the tree: the exposed signature may still be replayed by anyone who saw it, and a backup or another device cannot know about it, so only the rotation is signable now (R13). */
  private async burn(leaf: number, note: string): Promise<LeafRecord> {
    const r = await this.setStatus(leaf, ['reserved', 'signed', 'sent'], 'burned', { note });
    this.modeValue = 'recovery-only';
    return r;
  }
  async markSent(leaf: number, txSignature: string): Promise<LeafRecord> {
    this.live();
    return this.run(async () => {
      this.live();
      return this.setStatus(leaf, ['signed', 'sent'], 'sent', { txSignature });
    });
  }
  async markLanded(leaf: number): Promise<LeafRecord> {
    this.live();
    return this.run(async () => {
      this.live();
      return this.setStatus(leaf, ['signed', 'sent'], 'landed');
    });
  }
  /** Failure or unknown outcome: the leaf is never signed again, whatever the chain shows, and the tree is retired until its recovery message lands (section 10, R13). */
  async markBurned(leaf: number, note: string): Promise<LeafRecord> {
    this.live();
    return this.run(async () => {
      this.live();
      return this.burn(leaf, note);
    });
  }

  /**
   * Re-read the vault. `currentSlot` is this call's own chain-slot
   * observation, adopted at the start of the queued job, before the
   * pending-record loop, so the whole call uses one fresh slot throughout
   * (R25); it may not be older than the last slot this signer observed, or
   * the call rejects with `StaleSlot` before touching any record or marker.
   * Unresolved records resolve against the chain: a used leaf landed; a
   * rotation whose successor the chain now holds landed and makes the new
   * tree fresh on this device, provided this call's own `landed` write was
   * the one stored; anything else on a tree the chain has left, or past its
   * expiry, is burned. A record another writer resolved after the list was
   * read is left as stored (docs/17 L8). Then follow the chain's tree, recompute
   * the mode (marker present and unexpired, no burned leaf), and renew the
   * marker while normal (R20): a device that keeps syncing never lapses,
   * only one that stops.
   */
  async sync(accountData: Uint8Array, currentSlot: bigint): Promise<void> {
    this.live();
    return this.run(async () => {
      this.live();
      const slot = this.observeSlot(currentSlot);
      const snap = snapshot(this.programId, this.owner, accountData);
      const chainKey = hex(snap.verifyingKey);
      // Each resolution is one transition against the row as stored at its commit (docs/17 L8): a row another
      // writer resolved after the list was read is skipped, and the sync goes on.
      for (const r of await this.pending()) {
        if (snap.treeIndex === this.snap.treeIndex) {
          if (leafUsed(snap.usedLeaves, r.leaf)) await this.syncResolve(r, 'landed');
          else if (BigInt(r.validUntilSlot) < slot) await this.syncResolve(r, 'burned', 'expired before landing');
        } else if (snap.treeIndex === this.snap.treeIndex + 1 && r.nextKey === chainKey) {
          // The marker only for this call's own landed write: a row resolved elsewhere first (by the reconciliation
          // step, which may have declined a marker) grants nothing here (docs/17 section 4).
          const { written } = await this.syncResolve(r, 'landed');
          if (written) await VaultSigner.markTreeFresh(this.store, this.vaultId, snap.treeIndex, r.purpose === 'recovery' ? 'recovered' : 'rotated', slot, this.now);
        } else {
          await this.syncResolve(r, 'burned', `tree ${this.snap.treeIndex} left behind at tree ${snap.treeIndex}`);
        }
      }
      if (snap.treeIndex !== this.snap.treeIndex) {
        const verifyingKey = await this.backend.deriveTree(snap.treeIndex);
        if (!equalBytes(verifyingKey, snap.verifyingKey)) {
          await this.backend.wipeTree(snap.treeIndex);
          throw new SignerError('KeyMismatch', `the chain's key is not derive(${snap.treeIndex}) of this mnemonic`);
        }
        const previous = this.snap.treeIndex;
        await this.backend.wipeTree(previous);
        // A successor derived for a rotation that never landed (the chain skipped past t + 1) is dead too.
        if (this.successorCache && snap.treeIndex !== previous + 1) await this.backend.wipeTree(previous + 1);
        this.verifyingKeyBytes = verifyingKey;
        this.successorCache = undefined;
      }
      this.snap = snap;
      this.modeValue = await modeFor(this.store, this.vaultIdHex, snap.treeIndex, slot);
      if (this.modeValue === 'normal') {
        const marker = await this.store.getMarker(this.vaultIdHex, snap.treeIndex);
        if (marker) await this.store.putMarker({ ...marker, validUntilSlot: (slot + MARKER_LIFETIME_SLOTS).toString() });
      }
    });
  }

  /**
   * The reconciliation candidates (docs/17 section 3): this vault's
   * unresolved records that carry `nextKey`, on trees max(0, T - depth) to
   * T - 1, where T is the signer's tree. Read-only.
   */
  async earlierCandidates(depth: number = EARLIER_TREES): Promise<LeafRecord[]> {
    this.live();
    const out: LeafRecord[] = [];
    const T = this.snap.treeIndex;
    for (let t = Math.max(0, T - depth); t < T; t++) out.push(...(await this.candidatesOn(t)));
    return out;
  }
  private async candidatesOn(t: number): Promise<LeafRecord[]> {
    return (await this.store.listRecords(this.vaultIdHex, t)).filter((r) => UNRESOLVED.includes(r.status) && r.nextKey !== undefined);
  }

  /**
   * Reconcile the records on trees the chain has left (R69; docs/17 sections
   * 3 to 5), in one queued job. The job checks `live()` at its start and
   * before each write, adopts `currentSlot` before any write (R25), and
   * writes nothing when the snapshot is at another tree than the signer's or
   * is another vault's (`vault_id`). Per tree t from T - 8 up to T - 1 it
   * re-reads the candidates and judges each `sent` one with `appliedBy`
   * against the evidence read for the `txSignature` it still holds. With no
   * record proven, nothing is written for the tree. With two proven, or
   * with T = t + 1 and a chain key other than the proven `nextKey`, nothing
   * is written either (row 5). With one proven, in this order (L7): every
   * other candidate of the tree is burned, `not applied: tree <t> moved on
   * through leaf <L>` (row 6, 2.5); the marker for T is written from the
   * landing slot S when M1-M6 all hold (row 1); then the proven record moves
   * `sent` to `landed`. Each record write is one store transition that sets
   * only the status, and the note of a burn (L2, L8); a row another writer
   * resolved first is left as stored, outcome `resolved-elsewhere`. The step
   * never signs, derives, wipes, inserts or deletes, and never touches the
   * current tree's records. The mode is then recomputed.
   */
  async resolveEarlier(input: ResolveEarlierInput): Promise<EarlierOutcome[]> {
    this.live();
    return this.run(async () => {
      this.live();
      const slot = this.observeSlot(input.currentSlot);
      const snap = snapshot(this.programId, this.owner, input.accountData);
      const T = this.snap.treeIndex;
      const outcomes: EarlierOutcome[] = [];
      const unchanged = (r: LeafRecord, reason: EarlierUnchangedReason) => outcomes.push({ key: r.key, treeIndex: r.treeIndex, leaf: r.leaf, outcome: 'unchanged', reason });
      if (snap.treeIndex !== T || !equalBytes(snap.vaultId, this.vaultId)) {
        const reason = snap.treeIndex !== T ? 'tree-mismatch' : 'vault-mismatch';
        for (let t = Math.max(0, T - EARLIER_TREES); t < T; t++) for (const r of await this.candidatesOn(t)) unchanged(r, reason);
        return outcomes;
      }
      for (let t = Math.max(0, T - EARLIER_TREES); t < T; t++) {
        const judged = (await this.candidatesOn(t)).map((r) => this.judgeEarlier(r, input.evidence));
        const proven = judged.filter((j): j is Extract<Judged, { proven: true }> => j.proven);
        if (proven.length === 0) {
          for (const j of judged) unchanged(j.r, (j as Extract<Judged, { proven: false }>).reason);
          continue;
        }
        const p = proven[0]!;
        if (proven.length > 1 || (T === t + 1 && hex(snap.verifyingKey) !== p.r.nextKey)) {
          for (const j of judged) unchanged(j.r, 'contradiction');
          continue;
        }
        // Row 6: tree t had one transition, and it was p's (2.5).
        const note = notAppliedNote(t, p.r.leaf);
        for (const j of judged) {
          if (j === p) continue;
          this.live();
          const { written } = await this.transition(j.r.key, resolverStep('burned', note));
          if (written) outcomes.push({ key: j.r.key, treeIndex: t, leaf: j.r.leaf, outcome: 'not-applied' });
          else unchanged(j.r, 'resolved-elsewhere');
        }
        // M1-M6: own proof on T - 1; the transition's result (tag 1, key = nextKey, this vault: checked above);
        // nothing landed on T since; landed inside the lifetime; no marker and no record on T in this ledger.
        let marker = false;
        if (
          T === t + 1 &&
          snap.tag === TAG_VAULT &&
          usedCount(snap.usedLeaves) === 0 &&
          p.landingSlot + MARKER_LIFETIME_SLOTS >= slot &&
          (await this.store.getMarker(this.vaultIdHex, T)) === undefined &&
          (await this.store.listRecords(this.vaultIdHex, T)).length === 0
        ) {
          this.live();
          await VaultSigner.markTreeFresh(this.store, this.vaultId, T, p.r.purpose === 'recovery' ? 'recovered' : 'rotated', p.landingSlot, this.now);
          marker = true;
        }
        this.live();
        const { written } = await this.transition(p.r.key, resolverStep('landed'));
        if (written) outcomes.push({ key: p.r.key, treeIndex: t, leaf: p.r.leaf, outcome: marker ? 'landed-marker' : 'landed' });
        else unchanged(p.r, 'resolved-elsewhere');
      }
      this.modeValue = await modeFor(this.store, this.vaultIdHex, T, slot);
      return outcomes;
    });
  }

  /** One candidate's proof (docs/17 2.3, 2.4, rows 7-12): only a `sent` record with a `txSignature` and evidence read for that signature is judged. */
  private judgeEarlier(r: LeafRecord, evidence: ReadonlyMap<string, EarlierEvidence>): Judged {
    if (r.status !== 'sent') return { r, proven: false, reason: 'not-sent' };
    if (r.txSignature === undefined) return { r, proven: false, reason: 'no-tx-signature' };
    const ev = evidence.get(r.key);
    if (!ev) return { r, proven: false, reason: 'no-evidence' };
    if (ev.txSignature !== r.txSignature) return { r, proven: false, reason: 'stale-evidence' };
    const verdict = appliedBy(r, ev, { programId: this.programId, vaultId: this.vaultId, vaultAddress: this.vaultAddress });
    return verdict.applied ? { r, proven: true, landingSlot: verdict.landingSlot } : { r, proven: false, reason: verdict.reason };
  }

  /**
   * Terminal (R18/R21): sets `closing` synchronously, so every call made
   * from this instant rejects with `Closed`, whether it is new or was
   * already queued behind in-flight work. The wipe itself is queued behind
   * whatever job is currently running, so a job that started before
   * `close()` was called drains to completion and its result is delivered
   * to its caller; nothing signs after the wipe runs. The vendored key
   * object keeps its own copy of the chain starts, which this code cannot
   * overwrite; dropping every reference to the signer is what lets it be
   * collected.
   */
  close(): Promise<void> {
    this.closing = true;
    return this.run(async () => {
      this.closed = true;
      await this.backend.wipeTrees();
    });
  }
}
